import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { CheckCircle, ArrowRight, ArrowLeft, Loader2, Lock } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

export default function BookingFlow() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [caravan, setCaravan] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Guest form
  const [guest, setGuest] = useState({ name: '', email: '', phone: '', address: '' });
  const [emailOtp, setEmailOtp] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpMsg, setOtpMsg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/caravans/${slug}`),
      api.get(`/caravans/${slug}/availability`)
    ]).then(([cRes, aRes]) => {
      setCaravan(cRes.data);
      const allDates = [...(aRes.data.booked_dates || []), ...(aRes.data.blocked_dates || [])];
      setBookedDates(allDates.map(d => new Date(d)));
      const ci = searchParams.get('checkin');
      const co = searchParams.get('checkout');
      if (ci && co) {
        setDateRange({ from: new Date(ci), to: new Date(co) });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug, searchParams]);

  useEffect(() => {
    if (dateRange.from && dateRange.to && caravan) {
      api.get(`/pricing/calculate?caravan_id=${caravan.id}&check_in=${dateRange.from.toISOString().split('T')[0]}&check_out=${dateRange.to.toISOString().split('T')[0]}`)
        .then(r => setPriceInfo(r.data)).catch(() => {});
    }
  }, [dateRange, caravan]);

  const sendOtp = async (type) => {
    setOtpSending(true);
    setOtpMsg('');
    try {
      const contact = type === 'email' ? guest.email : guest.phone;
      await api.post('/otp/send', { contact, type });
      setOtpMsg(`Code sent to ${contact}`);
    } catch (e) {
      setOtpMsg(formatApiError(e.response?.data?.detail));
    }
    setOtpSending(false);
  };

  const verifyOtp = async (type) => {
    setOtpMsg('');
    try {
      const contact = type === 'email' ? guest.email : guest.phone;
      const code = type === 'email' ? emailOtp : phoneOtp;
      const res = await api.post('/otp/verify', { contact, code, type });
      if (res.data.verified) {
        type === 'email' ? setEmailVerified(true) : setPhoneVerified(true);
        setOtpMsg(`${type === 'email' ? 'Email' : 'Phone'} verified!`);
      }
    } catch (e) {
      setOtpMsg(formatApiError(e.response?.data?.detail));
    }
  };

  const handlePayment = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/bookings/create', {
        caravan_id: caravan.id,
        check_in: dateRange.from.toISOString().split('T')[0],
        check_out: dateRange.to.toISOString().split('T')[0],
        guest_name: guest.name,
        guest_email: guest.email,
        guest_phone: guest.phone,
        guest_address: guest.address,
        num_guests: 1,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Booking failed. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B726A]">Loading...</div>;
  if (!caravan) return <div className="min-h-screen flex items-center justify-center text-[#6B726A]">Caravan not found</div>;

  const steps = ['Select Dates', 'Guest Details', 'Payment'];
  const canProceedStep1 = dateRange.from && dateRange.to && priceInfo;
  const canProceedStep2 = guest.name && guest.email && guest.phone && guest.address && emailVerified && phoneVerified;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="booking-flow">
      {/* Progress */}
      <div className="border-b border-[#E6D5B8] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center gap-4" data-testid="booking-steps">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step > i + 1 ? 'bg-[#2D6A4F] text-white' : step === i + 1 ? 'bg-[#CC5A3D] text-white' : 'bg-[#F0EBE1] text-[#6B726A]'
                }`}>
                  {step > i + 1 ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-sm hidden sm:inline ${step === i + 1 ? 'text-[#1F2A25] font-medium' : 'text-[#6B726A]'}`}>{s}</span>
                {i < 2 && <div className="w-8 h-px bg-[#E6D5B8]" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-2">Book {caravan.name}</h1>
        <p className="text-sm text-[#6B726A] mb-8">{caravan.park?.name}</p>

        {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded" data-testid="booking-error">{error}</div>}

        {/* Step 1: Dates */}
        {step === 1 && (
          <div data-testid="booking-step-1">
            <h2 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Select your dates</h2>
            <div className="bg-white border border-[#E6D5B8] p-6 rounded-lg mb-6">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
                disabled={[{ before: new Date() }, ...bookedDates]}
                className="mx-auto"
                data-testid="booking-calendar"
              />
            </div>
            {priceInfo && (
              <div className="bg-[#F0EBE1] border border-[#E6D5B8] p-5 rounded-lg mb-6" data-testid="booking-price-summary">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#6B726A]">{priceInfo.nights} night{priceInfo.nights > 1 ? 's' : ''}</span>
                  <span className="font-medium">&pound;{priceInfo.total_price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#6B726A]">Security deposit (separate)</span>
                  <span className="text-[#6B726A]">&pound;{priceInfo.deposit_amount.toFixed(2)}</span>
                </div>
                <div className="border-t border-[#E6D5B8] pt-2 mt-2 flex justify-between">
                  <span className="font-medium">Booking total</span>
                  <span className="text-xl font-heading font-medium text-[#CC5A3D]">&pound;{priceInfo.total_price.toFixed(2)}</span>
                </div>
              </div>
            )}
            <Button onClick={() => setStep(2)} disabled={!canProceedStep1} className="bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm px-8 py-3 disabled:opacity-50" data-testid="step1-next-btn">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Guest Details */}
        {step === 2 && (
          <div data-testid="booking-step-2">
            <h2 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Guest Details</h2>
            <div className="bg-white border border-[#E6D5B8] p-6 rounded-lg space-y-5">
              <div>
                <Label className="text-sm text-[#1F2A25]">Full Name *</Label>
                <Input value={guest.name} onChange={e => setGuest({...guest, name: e.target.value})} placeholder="John Smith" className="mt-1 border-[#E6D5B8] focus:border-[#1E3F33]" data-testid="guest-name-input" />
              </div>

              <div>
                <Label className="text-sm text-[#1F2A25]">Email Address *</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={guest.email} onChange={e => { setGuest({...guest, email: e.target.value}); setEmailVerified(false); }} placeholder="john@example.com" className="border-[#E6D5B8] focus:border-[#1E3F33]" disabled={emailVerified} data-testid="guest-email-input" />
                  {!emailVerified && (
                    <Button onClick={() => sendOtp('email')} disabled={!guest.email || otpSending} variant="outline" className="border-[#1E3F33] text-[#1E3F33] whitespace-nowrap" data-testid="send-email-otp-btn">
                      {otpSending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Code'}
                    </Button>
                  )}
                  {emailVerified && <CheckCircle className="w-6 h-6 text-[#2D6A4F] self-center flex-shrink-0" />}
                </div>
                {!emailVerified && guest.email && (
                  <div className="flex gap-2 mt-2">
                    <Input value={emailOtp} onChange={e => setEmailOtp(e.target.value)} placeholder="Enter 6-digit code" maxLength={6} className="border-[#E6D5B8]" data-testid="email-otp-input" />
                    <Button onClick={() => verifyOtp('email')} disabled={emailOtp.length < 6} variant="outline" className="border-[#1E3F33] text-[#1E3F33]" data-testid="verify-email-otp-btn">Verify</Button>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm text-[#1F2A25]">Phone Number *</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={guest.phone} onChange={e => { setGuest({...guest, phone: e.target.value}); setPhoneVerified(false); }} placeholder="+44 7700 900000" className="border-[#E6D5B8] focus:border-[#1E3F33]" disabled={phoneVerified} data-testid="guest-phone-input" />
                  {!phoneVerified && (
                    <Button onClick={() => sendOtp('phone')} disabled={!guest.phone || otpSending} variant="outline" className="border-[#1E3F33] text-[#1E3F33] whitespace-nowrap" data-testid="send-phone-otp-btn">
                      {otpSending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Code'}
                    </Button>
                  )}
                  {phoneVerified && <CheckCircle className="w-6 h-6 text-[#2D6A4F] self-center flex-shrink-0" />}
                </div>
                {!phoneVerified && guest.phone && (
                  <div className="flex gap-2 mt-2">
                    <Input value={phoneOtp} onChange={e => setPhoneOtp(e.target.value)} placeholder="Enter code (use 123456)" maxLength={6} className="border-[#E6D5B8]" data-testid="phone-otp-input" />
                    <Button onClick={() => verifyOtp('phone')} disabled={phoneOtp.length < 6} variant="outline" className="border-[#1E3F33] text-[#1E3F33]" data-testid="verify-phone-otp-btn">Verify</Button>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm text-[#1F2A25]">Address *</Label>
                <Input value={guest.address} onChange={e => setGuest({...guest, address: e.target.value})} placeholder="123 Main St, London, UK" className="mt-1 border-[#E6D5B8] focus:border-[#1E3F33]" data-testid="guest-address-input" />
              </div>

              {otpMsg && <p className="text-sm text-[#6B726A]" data-testid="otp-message">{otpMsg}</p>}
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => setStep(1)} variant="outline" className="border-[#E6D5B8] text-[#1F2A25]" data-testid="step2-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!canProceedStep2} className="bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm px-8 disabled:opacity-50" data-testid="step2-next-btn">
                Review Booking <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Pay */}
        {step === 3 && (
          <div data-testid="booking-step-3">
            <h2 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Review & Pay</h2>
            <div className="bg-white border border-[#E6D5B8] p-6 rounded-lg space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-[#6B726A]">Property</span><p className="font-medium text-[#1F2A25]">{caravan.name}</p></div>
                <div><span className="text-[#6B726A]">Park</span><p className="font-medium text-[#1F2A25]">{caravan.park?.name}</p></div>
                <div><span className="text-[#6B726A]">Check-in</span><p className="font-medium text-[#1F2A25]">{dateRange.from?.toLocaleDateString('en-GB')}</p></div>
                <div><span className="text-[#6B726A]">Check-out</span><p className="font-medium text-[#1F2A25]">{dateRange.to?.toLocaleDateString('en-GB')}</p></div>
                <div><span className="text-[#6B726A]">Guest</span><p className="font-medium text-[#1F2A25]">{guest.name}</p></div>
                <div><span className="text-[#6B726A]">Email</span><p className="font-medium text-[#1F2A25]">{guest.email}</p></div>
              </div>
              <div className="border-t border-[#E6D5B8] pt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#6B726A]">{priceInfo?.nights} nights</span>
                  <span className="font-medium">&pound;{priceInfo?.total_price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-heading font-medium mt-2">
                  <span className="text-[#1F2A25]">Total to pay now</span>
                  <span className="text-[#CC5A3D]">&pound;{priceInfo?.total_price.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" className="border-[#E6D5B8] text-[#1F2A25]" data-testid="step3-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={handlePayment} disabled={submitting} className="bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm px-8 flex-1 disabled:opacity-50" data-testid="pay-now-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                {submitting ? 'Processing...' : `Pay £${priceInfo?.total_price.toFixed(2)}`}
              </Button>
            </div>
            <p className="text-xs text-center text-[#6B726A] mt-3 flex items-center justify-center gap-1">
              <Lock className="w-3 h-3" /> Secure payment powered by Stripe
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
